import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal } from "./common";
import XeroSavings from "./XeroSavings";
import SupportCaveat from "./SupportCaveat";

// The cost answer for people arriving from "how much is xero per month" searches.
// It sits high on the page on purpose: they leave rather than scroll for it.
// Xero's list price sits alongside what the same plan costs through CASS, which is
// the list price less the partner discount held in the admin.
const cassPrice = (price, pct) => {
  const m = String(price || "").match(/(\d+(?:\.\d+)?)/);
  const d = Number(pct);
  if (!m || !d) return "";
  return `£${(parseFloat(m[1]) * (1 - d / 100)).toFixed(2)}`;
};

export const XeroCostAnswer = ({ cfg }) => {
  const plans = (cfg.plans || []).filter((p) => p && p.name);
  const guide = cfg.plan_guide;
  const discount = Number(cfg.cass_discount_pct) || 0;
  const showCass = discount > 0 && plans.some((p) => cassPrice(p.price, discount));
  const checked = cfg.checked_on
    ? new Date(cfg.checked_on).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <section className="bg-white border-b border-slate-100" data-testid="xero-cost-answer">
      <div className="max-w-5xl mx-auto px-6 py-14 md:py-20">
        <Reveal>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy">{cfg.heading}</h2>
          <p className="mt-4 text-slate-700 text-base md:text-lg leading-relaxed" data-testid="xero-cost-answer-text">
            {cfg.answer}
          </p>
        </Reveal>

        {plans.length > 0 && (
          <Reveal delay={80}>
            <div className="mt-8 overflow-x-auto">
              <table className="w-full text-left border-collapse" data-testid="xero-plans-table">
                <caption className="sr-only">Xero UK plans, Xero's list price and the price through CASS, excluding VAT</caption>
                <thead>
                  <tr className="border-b border-slate-200">
                    <th scope="col" className="py-3 pr-4 text-xs font-bold uppercase tracking-widest text-slate-400">Plan</th>
                    <th scope="col" className="py-3 pr-4 text-xs font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">
                      {showCass ? "Xero direct" : "Per month"}
                    </th>
                    {showCass && (
                      <th scope="col" className="py-3 pr-4 text-xs font-bold uppercase tracking-widest text-orange whitespace-nowrap">
                        {cfg.cass_price_label || "Through CASS"}
                      </th>
                    )}
                    <th scope="col" className="py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Suits</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => (
                    <tr key={p.name} className="border-b border-slate-100 align-top" data-testid={`xero-plan-${p.name.toLowerCase()}`}>
                      <th scope="row" className="py-4 pr-4 font-display font-bold text-navy whitespace-nowrap">{p.name}</th>
                      <td className={`py-4 pr-4 font-display font-bold whitespace-nowrap ${showCass ? "text-slate-400 line-through decoration-slate-300" : "text-navy"}`}>
                        {p.price} {!showCass && <span className="text-xs font-semibold text-slate-400">+ VAT</span>}
                      </td>
                      {showCass && (
                        <td className="py-4 pr-4 font-display font-bold text-navy whitespace-nowrap" data-testid={`xero-cass-price-${p.name.toLowerCase()}`}>
                          {cassPrice(p.price, discount) || p.price}{" "}
                          <span className="text-xs font-semibold text-slate-400">+ VAT</span>
                        </td>
                      )}
                      <td className="py-4 text-sm text-slate-600 leading-relaxed">{p.suits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-slate-400 leading-relaxed" data-testid="xero-price-note">
              {showCass && cfg.cass_price_note ? `${cfg.cass_price_note} ` : ""}
              {cfg.vat_note} {cfg.price_note}
              {checked && <> Prices last checked {checked}.</>}
            </p>
            {showCass && <XeroSavings cfg={cfg} className="mt-8" />}
          </Reveal>
        )}

        {guide && (guide.steps || []).length > 0 && (
          <Reveal delay={110}>
            <div className="mt-10 rounded-2xl bg-slate-50 border border-slate-100 p-6 md:p-8" data-testid="xero-plan-guide">
              <h3 className="font-display font-bold text-xl text-navy">{guide.heading}</h3>
              {guide.intro && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{guide.intro}</p>}
              <dl className="mt-6 space-y-5">
                {(guide.steps || []).filter((s) => s && s.q).map((s, i) => (
                  <div key={i} data-testid={`xero-guide-step-${i}`}>
                    <dt className="font-display font-bold text-navy text-sm md:text-base">{s.q}</dt>
                    <dd className="mt-1 text-sm text-slate-600 leading-relaxed">{s.a}</dd>
                  </div>
                ))}
              </dl>
              {guide.note && <p className="mt-6 pt-5 border-t border-slate-200 text-sm text-slate-600 leading-relaxed">{guide.note}</p>}
            </div>
          </Reveal>
        )}

        <Reveal delay={140}>
          <div className="mt-8 rounded-2xl border border-orange/25 bg-orange/5 p-6" data-testid="xero-partner-note">
            <p className="flex gap-3 text-navy font-semibold leading-relaxed">
              <CheckCircle2 size={20} className="text-orange shrink-0 mt-0.5" />
              <span>{cfg.partner_note}</span>
            </p>
            {cfg.billing_note && <p className="mt-3 pl-8 text-sm text-slate-600 leading-relaxed">{cfg.billing_note}</p>}
            {cfg.support_caveat && (
              <SupportCaveat text={cfg.support_caveat} className="mt-3 pl-8" />
            )}
            <Link
              to="/contact?service=Xero%20Subscription"
              data-testid="xero-cost-cta"
              className="mt-5 ml-8 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-6 py-3 rounded-full transition-all hover:-translate-y-0.5"
            >
              {cfg.cta_label || "Ask what Xero would cost you"} <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default XeroCostAnswer;
